import type { TenderSection } from "./api";

/** As the web app (utils/lengthConversions.ts). */
const WORDS_PER_PAGE = 400;
const CHARACTERS_PER_WORD = 6;

/**
 * A section's length limit as the tender states it, e.g. "1,200 words" or
 * "2 pages". `limit_value`/`limit_type` are authoritative; legacy sections
 * only carry `word_count`, in words. Mirrors the web app's
 * `resolveLimitValue`.
 * @param section - The outline section.
 * @returns The label, or null when the section has no limit.
 */
export type LimitType = "words" | "pages" | "characters";

/**
 * A section's limit in its own unit, or null when it has none: `limit_value`
 * when set, otherwise converted from the legacy `word_count`.
 * @param section - The outline section.
 * @returns The value, e.g. 1200 (words) or 2.5 (pages).
 */
export const limitValueOf = (section: TenderSection): number | null => {
  const type = section.limit_type ?? "words";
  if (typeof section.limit_value === "number" && section.limit_value > 0) return section.limit_value;
  if (typeof section.word_count !== "number" || section.word_count <= 0) return null;
  return type === "pages"
    ? Math.max(1, Math.round(section.word_count / WORDS_PER_PAGE))
    : type === "characters"
      ? section.word_count * CHARACTERS_PER_WORD
      : section.word_count;
};

export const formatLimit = (section: TenderSection): string | null => {
  const type = section.limit_type ?? "words";
  const value = limitValueOf(section);
  if (value === null) return null;
  const unit = type === "pages" ? (value === 1 ? "page" : "pages") : type === "characters" ? "chars" : "words";
  return `${value.toLocaleString()} ${unit}`;
};
